import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSnackbar } from 'notistack';
import { Box, Button, Card, Stack, Switch, TextField, Typography } from '@curves/ui-base';

import useProductApi from '@Apis/useProductApi';
import useValidationErrors from '@Util/hook/useValidationErrors';
import useMessageDialog from '@Util/hook/useMessageDialog';
import CategoryCascadeSelect from '@Local/Product/Form/CategoryCascadeSelect';
import VariantCard from '@Local/Product/Form/VariantCard';
import { PRICE_FIELDS } from '@Local/Product/priceFields';
import {
    buildProductPayload,
    createClientKey,
    createEmptyVariant,
    findCategoryPath,
    getEffectiveCategoryID,
    isVariantNameRequired,
    validateProductForm,
} from '@Local/Product/Form/productFormValidation';

// 取出後端回傳的某張規格卡片欄位錯誤（useValidationErrors 會把 variants[1].sku 轉成 variants1.sku）
const getServerVariantErrors = (validationErrors, index) => validationErrors?.[`variants${index}`] || {};

/**
 * 新增／編輯共用表單，`initialProduct` 為 null 時為新增
 *
 * @param {object} props
 * @param {number|string} [props.productID] 編輯時的商品 ID，無值為新增
 * @param {object|null} [props.initialProduct] 初始資料（商品明細回應格式），`null` 為新增
 * @param {Array} [props.categoryOptions] 分類樹選項
 * @param {(payload: object) => Promise<any>} props.submitApi 送出時呼叫（新增傳 `postProductApi`，編輯由呼叫端綁定 `putProductApi`）
 * @param {(resp: any) => void} props.onSuccess 送出成功後呼叫，由呼叫端決定導頁
 * @param {() => void} props.onCancel 按下取消時呼叫，由呼叫端決定導頁
 */
const ProductForm = ({ productID, initialProduct = null, categoryOptions = [], submitApi, onSuccess, onCancel }) => {
    const { validationErrors, updateValidationErrors, clearValidationErrors } = useValidationErrors();
    const { enqueueSnackbar } = useSnackbar();
    const setMessageDialog = useMessageDialog();
    const { putProductEnabledApi } = useProductApi();

    // 商品名稱輸入框（非受控，送出時讀值）
    const productNameRef = useRef(null);
    // 是否上架開關（非受控，送出時讀值）
    const isEnabledRef = useRef(null);

    // 已選分類路徑（第一層到目前選到的層），編輯時由明細的 categoryID 帶入
    const [categoryPath, setCategoryPath] = useState([]);

    // 分類樹載入後，依明細的 categoryID 帶入初始分類路徑，已有選擇時不覆蓋
    const applyInitialCategoryPath = useCallback(() => {
        const initialCategoryID = initialProduct?.categoryID;
        if (!initialCategoryID) return;
        setCategoryPath((prev) => (prev.length > 0 ? prev : findCategoryPath(categoryOptions, initialCategoryID)));
    }, [categoryOptions, initialProduct?.categoryID]);

    useEffect(() => {
        applyInitialCategoryPath();
    }, [applyInitialCategoryPath]);

    // 規格卡片清單，isExisting 標記已存在的規格（不可刪除，僅前端使用）
    const [variants, setVariants] = useState(() => {
        const initialVariants = (initialProduct?.variants || []).map((variant) => ({
            clientKey: createClientKey(),
            isExisting: true,
            variantName: variant.variantName ?? '',
            sku: variant.sku ?? '',
            ...Object.fromEntries(PRICE_FIELDS.map(({ field }) => [field, variant[field] ?? ''])),
            unitAmount: variant.unitAmount ?? '',
        }));
        return initialVariants.length > 0 ? initialVariants : [createEmptyVariant()];
    });
    // 主規格卡片的索引，編輯時依明細的 mainSKU 帶入，找不到為 null
    const [defaultVariantIndex, setDefaultVariantIndex] = useState(() => {
        const index = (initialProduct?.variants || []).findIndex((variant) => variant.sku === initialProduct?.mainSKU);
        return index >= 0 ? index : null;
    });
    // 前端檢核錯誤
    const [formErrors, setFormErrors] = useState({
        category: '',
        variants: variants.map(() => ({})),
    });

    // 新增一張空白規格卡片
    const handleAddVariant = () => {
        setVariants((prev) => [...prev, createEmptyVariant()]);
        setFormErrors((prev) => ({ ...prev, variants: [...prev.variants, {}] }));
    };

    // 刪除規格卡片；若刪到主規格則清空主規格選擇
    const handleRemoveVariant = (index) => {
        const nextVariants = variants.filter((_, i) => i !== index);
        setVariants(nextVariants);
        setFormErrors((prev) => {
            const nextVariantErrors = prev.variants.filter((_, i) => i !== index);
            return {
                ...prev,
                // 刪到規格名稱變成非必填時，清掉殘留的「請輸入規格名稱」錯誤
                variants: isVariantNameRequired(nextVariants)
                    ? nextVariantErrors
                    : nextVariantErrors.map(({ variantName, ...fieldErrors }) => fieldErrors),
            };
        });
        setDefaultVariantIndex((prev) => {
            if (prev === null) return null;
            if (prev === index) return null; // 刪除主規格卡片後不自動改選
            if (prev > index) return prev - 1; // 被刪卡片之後的主規格索引往前移
            return prev;
        });
    };

    // 更新指定規格卡片的單一欄位
    const handleVariantFieldChange = (index, field, value) => {
        setVariants((prev) => prev.map((variant, i) => (i === index ? { ...variant, [field]: value } : variant)));
    };

    // 設定主規格
    const handleSelectDefaultVariant = (index) => {
        setDefaultVariantIndex(index);
    };

    // 將此卡片的價格與單位個數套用到其他卡片，規格名稱與 SKU 不動
    const handleApplyToAll = (sourceIndex) => {
        setVariants((prev) => {
            const source = prev[sourceIndex];
            return prev.map((variant, i) =>
                i === sourceIndex
                    ? variant
                    : {
                          ...variant,
                          ...Object.fromEntries(PRICE_FIELDS.map(({ field }) => [field, source[field]])),
                          unitAmount: source.unitAmount,
                      },
            );
        });
    };

    // 以目前表單內容組出送出的 payload
    const buildPayload = () =>
        buildProductPayload({
            productName: productNameRef.current?.getResult(),
            categoryID: getEffectiveCategoryID(categoryOptions, categoryPath),
            variants,
            defaultVariantIndex,
        });

    // 送出表單，成功後同步上下架
    const handleSubmit = async (isEnabledValue) => {
        const resp = await submitApi(buildPayload());

        if (resp?.isError) {
            // 後端欄位驗證錯誤，標示到對應欄位
            updateValidationErrors(resp.result);
            return;
        }
        if (!resp) return; // 失敗：useAxios 已顯示 snackbar，保留表單內容

        clearValidationErrors();
        // 後端新增預設下架、編輯不處理上下架，所以另外呼叫
        if (productID) await putProductEnabledApi(productID, isEnabledValue);
        else if (isEnabledValue) await putProductEnabledApi(resp, true);
        if (typeof onSuccess === 'function') onSuccess(resp);
    };

    // 送出前檢核，上架需先開確認彈窗，下架直接送出
    const handleValidateAndSubmit = () => {
        const { productName, variantCount, defaultVariant, hasError, ...nextFormErrors } = validateProductForm({
            productName: productNameRef.current?.getResult(),
            categoryOptions,
            categoryPath,
            variants,
            defaultVariantIndex,
        });
        productNameRef.current?.setError(Boolean(productName), '請輸入商品名稱');
        setFormErrors(nextFormErrors);
        // 規格數量與主規格的錯誤沒有對應欄位，改用提示訊息
        if (variantCount || defaultVariant) enqueueSnackbar(variantCount || defaultVariant, { variant: 'error' });

        if (hasError) return;

        // 上架需先確認，下架直接送出
        const isEnabledValue = Boolean(isEnabledRef.current?.checked);
        if (isEnabledValue) {
            setMessageDialog({
                open: true,
                title: '上架確認',
                msg: '確定要以上架狀態儲存此商品嗎？',
                buttonText: '確認上架',
                onConfirm: () => handleSubmit(true),
            });
        } else {
            handleSubmit(false);
        }
    };

    return (
        <Box>
            <Stack direction="column" spacing={3}>
                <Box>
                    <Card className="p-3">
                        <Card.CardContent className="p-0">
                            <Typography variant="h6" className="text-primary font-weight-bold mb-2">
                                基本資料
                            </Typography>
                            <Stack direction="column" spacing={2}>
                                <TextField
                                    key={initialProduct?.productName ?? ''}
                                    ref={productNameRef}
                                    label="商品名稱"
                                    defaultValue={initialProduct?.productName ?? ''}
                                    fullWidth
                                    required
                                />
                                <CategoryCascadeSelect
                                    categories={categoryOptions}
                                    value={categoryPath}
                                    onChange={setCategoryPath}
                                    isError={!!formErrors.category}
                                    helperText={formErrors.category}
                                />
                                <Stack direction="row" alignItems="center" spacing={1}>
                                    <Typography className="mb-0">是否上架</Typography>
                                    <Switch
                                        key={String(initialProduct?.isEnabled ?? false)}
                                        inputProps={{ ref: isEnabledRef }}
                                        defaultChecked={initialProduct?.isEnabled ?? false}
                                    />
                                </Stack>
                            </Stack>
                        </Card.CardContent>
                    </Card>
                </Box>

                <Box>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" className="mb-2">
                        <Typography variant="h6" className="text-primary font-weight-bold mb-0">
                            商品規格
                        </Typography>
                        <Button variant="outlined" color="primary" onClick={handleAddVariant}>
                            ＋新增規格
                        </Button>
                    </Stack>


                    {variants.map((variant, index) => (
                        <VariantCard
                            key={variant.clientKey}
                            index={index}
                            variant={variant}
                            isDefault={defaultVariantIndex === index}
                            canRemove={!variant.isExisting}
                            isVariantNameRequired={isVariantNameRequired(variants)}
                            errors={{
                                ...formErrors.variants[index],
                                ...getServerVariantErrors(validationErrors, index),
                            }}
                            onFieldChange={(field, value) => handleVariantFieldChange(index, field, value)}
                            onSelectDefault={() => handleSelectDefaultVariant(index)}
                            onRemove={variant.isExisting ? undefined : () => handleRemoveVariant(index)}
                            onApplyToAll={() => handleApplyToAll(index)}
                        />
                    ))}
                </Box>
            </Stack>

            <Stack direction="row" justifyContent="end" spacing={2} className="mt-3">
                <Button variant="outlined" color="primary" onClick={onCancel}>
                    取消
                </Button>
                <Button variant="contained" color="primary" onClick={handleValidateAndSubmit}>
                    送出
                </Button>
            </Stack>

        </Box>
    );
};

export default ProductForm;
